/**
 * Wall clock for the elapsed-time labels, re-rendering its caller on a tick.
 *
 * Two speeds, because the two jobs it does are not the same job. A running
 * timer wants {@link FAST_TICK}: after Resume the minute figure is the only
 * thing that says the clock restarted, and at 30s granularity it can sit
 * unchanged for half a minute — long enough for a tech to press Resume twice.
 * Everything else wants {@link SLOW_TICK}: a settled "logged 45m" does not
 * change between renders, so ticking it every second is 30× the re-renders
 * for the same pixels.
 *
 * Callers pass the speed they need — `useNow(running ? FAST_TICK : SLOW_TICK)`
 * — and the timer is re-armed when it changes, so a task going from
 * running to paused drops straight back to the cheap tick.
 *
 * Hoisted from three identical copies (Today, WO detail, the bottom bar) that
 * had all frozen at the slow tick, then again out of `apps/technical` when
 * management's review screens needed the same clock: a running labour segment
 * banks NO minutes until it is stopped, so every elapsed figure over there has
 * to be finished on the client too.
 */
import { useEffect, useState } from 'react'

/** A live timer — the label has to visibly move. */
export const FAST_TICK = 1_000
/** Anything settled — 30s is all a static minute figure needs. */
export const SLOW_TICK = 30_000

/**
 * Ticks on WALL-CLOCK boundaries — at every whole multiple of `intervalMs`
 * since the epoch — rather than every `intervalMs` from mount.
 *
 * `setInterval` would tie the phase to whenever the component happened to
 * mount, and two components showing the same elapsed figure rarely mount
 * together: a task row appears when the manager expands its service fold,
 * long after the header above it. Both floor to whole minutes, so a phase
 * offset leaves the two labels a minute apart for most of every minute —
 * on the same panel, about the same segment. Aligned, every consumer of a
 * given speed flips at the same instant, and the fast and slow speeds agree
 * with each other because 30s boundaries are also second boundaries.
 */
export function useNow(intervalMs: number = SLOW_TICK): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    // Re-sync immediately on a speed change: switching to the fast tick after
    // a Resume should not wait out the tail of a 30s boundary.
    setNow(Date.now())
    let id = 0
    const arm = () => {
      // Time left until the next boundary — a full interval when we are
      // sitting exactly on one, so a tick is never scheduled at zero delay.
      const untilBoundary = intervalMs - (Date.now() % intervalMs)
      id = window.setTimeout(() => {
        setNow(Date.now())
        arm()
      }, untilBoundary)
    }
    arm()
    return () => window.clearTimeout(id)
  }, [intervalMs])
  return now
}
